'use client';

import { createAutocomplete } from '@algolia/autocomplete-core';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SEARCH_PAGE_PATH } from '@libs/algolia';
import {
    trackLegacySearchEvent,
    trackSearchFlyoutClick,
    trackSubmitSearchEvent,
    useAnalyticsContext,
} from '@libs/analytics';
import { BREAKPOINTS, clsx, ErrorBoundary, stripHtml, useOptimizely, useWindowSize } from '@libs/common-next';

import { Panel } from './Panel';
import { useOptexSearchBiaGuest } from './Panel/optex-search-bia-guest';
import { useAlgoliaPlugins } from './plugins';
import { SearchBox } from './SearchBox';
import type { AutocompleteItem, AutocompleteProps, SetAutocompleteStateOptions } from './types';
import { checkForRedirects, getAutocompleteCollections } from './utils';
import './Autocomplete.scss';

export function Autocomplete({
    isOpen,
    isClearQueryOnCancel = false,
    className,
    productsHitPerPage = 3,
    isOptexSearchBiaGuestEnabled = false,
    ...autocompleteProps
}: AutocompleteProps) {
    const params = useSearchParams();
    const { setSearchStats, getSearchStats } = useAnalyticsContext();
    const { context } = useOptimizely();
    const currentQuery = stripHtml(params?.get('q') ?? '');
    const [cursor, setCursor] = useState(-1);

    const [autocompleteState, setAutocompleteState] = useState<SetAutocompleteStateOptions>({
        collections: [],
        isOpen: isOpen ?? false,
        context: {},
    });
    const [autocompleteQuery, setAutocompleteQuery] = useState<string>(currentQuery);
    // OptEx: SB-4665 — keep guest products mounted while the flyout opens and closes.
    const { data: optexProducts } = useOptexSearchBiaGuest(isOptexSearchBiaGuestEnabled);

    const isSearchFullscreen = useWindowSize() < BREAKPOINTS.lg;

    useEffect(() => {
        document.body.style.overflow = isSearchFullscreen && autocompleteState.isOpen ? 'hidden' : 'unset';
    }, [isSearchFullscreen, autocompleteState.isOpen]);

    const plugins = useAlgoliaPlugins(setAutocompleteState, setAutocompleteQuery, productsHitPerPage);

    const [searchTermValue, setSearchTermValue] = useState<string>('');
    const [searchAssistTermValue, setSearchAssistTermValue] = useState<string>('');
    const [searchAssistNameValue, setSearchAssistNameValue] = useState<string>('');

    const [isAssistTermModified, setIsAssistTermModified] = useState<boolean>(false);

    useEffect(() => {
        const handleAssistedSearchData = (data: Event) => {
            const { searchTerm, searchAssistTerm, searchAssistName } = (data as CustomEvent).detail;

            setSearchTermValue(() => searchTerm);
            setSearchAssistTermValue(() => searchAssistTerm);
            setSearchAssistNameValue(() => searchAssistName);
            setIsAssistTermModified(() => false);
        };

        window.addEventListener('getAssistedSearchData', handleAssistedSearchData);

        return () => {
            window.removeEventListener('getAssistedSearchData', handleAssistedSearchData);
        };
    }, []);

    const autocomplete = useMemo(
        () =>
            createAutocomplete<AutocompleteItem, React.SyntheticEvent, React.MouseEvent, React.KeyboardEvent>({
                ...autocompleteProps,
                initialState: { query: autocompleteQuery },
                // plugins are where most of our customizations will be.
                // either by using an existing algolia plugin or by creating a custom one
                // as it was made for product suggestions.
                plugins,
                openOnFocus: true,
                async onSubmit() {
                    let urlParams = `?q=${encodeURIComponent(autocompleteQuery.trim())}&st=manual`;
                    let searchType = 'manual';

                    if (isAssistTermModified) {
                        searchType = 'assisted';

                        trackSubmitSearchEvent(searchTermValue, searchAssistNameValue, searchAssistTermValue);

                        urlParams = `?q=${autocompleteQuery}&st=assisted&sa=${searchAssistNameValue}&sat=${searchAssistTermValue}&sterm=${searchTermValue}`;
                    } else {
                        trackSubmitSearchEvent(autocompleteQuery);
                    }

                    context?.trackEvent('_us_ca__user_searches__fxe__global___r__');

                    setSearchStats({
                        ...getSearchStats(),
                        searchTerm: autocompleteQuery,
                        searchType,
                    });

                    trackLegacySearchEvent(autocompleteQuery, searchAssistNameValue);

                    if (await checkForRedirects(autocompleteState, autocompleteQuery)) {
                        // do not continue if there is a redirect
                        return;
                    }

                    const url = `/${SEARCH_PAGE_PATH}${urlParams}`;
                    trackSearchFlyoutClick({
                        term: autocompleteQuery,
                        header: '',
                        url,
                    });

                    // if user is just running a simple text search, forward them to the search page
                    // TODO: router.push works but if user is already on search page it does not
                    // try to get router.push to work here instead.
                    window.location.href = url;

                    setAutocompleteState({
                        isOpen: false,
                        collections: [],
                        context: autocompleteState.context,
                    });
                },
                onStateChange({ state }) {
                    if (state.collections.length > 0) {
                        const collections = getAutocompleteCollections(state.collections);

                        setAutocompleteState({
                            ...state,
                            isOpen: collections.length > 0 ? state.isOpen : false,
                        });
                    }
                },
            }),
        [plugins]
    );

    const searchWrapper = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchWrapper.current?.contains(e.target as Node)) {
                return;
            }

            setAutocompleteState(prev => ({
                ...prev,
                isOpen: false,
            }));
        };

        document.addEventListener('click', handleClickOutside, true);

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [searchWrapper]);

    const collections = getAutocompleteCollections(autocompleteState.collections);

    const maxCursor = collections.reduce((acc, collection) => acc + collection.items.length, -1);

    // istanbul ignore next
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
        if (e.key === 'ArrowUp') {
            setCursor(prevState => (cursor > 0 ? prevState - 1 : maxCursor));
            e.preventDefault();
            return;
        }

        if (e.key === 'ArrowDown' && cursor < maxCursor) {
            setCursor(prevState => prevState + 1);
            e.preventDefault();
            return;
        }

        setCursor(-1);

        const searchInput = e.target as HTMLInputElement | HTMLButtonElement;
        const searchInputValue = searchInput?.value;

        if (searchInputValue === '') {
            setSearchTermValue(() => '');
            setSearchAssistTermValue(() => '');
            setSearchAssistNameValue(() => '');
            setIsAssistTermModified(() => false);
        } else if (searchTermValue !== '' && searchTermValue !== searchInputValue) {
            setIsAssistTermModified(() => true);
        }
    };

    const classNames = clsx('autocomplete', autocompleteState.isOpen && 'autocomplete--open', className);

    return (
        <div
            className={classNames}
            data-testid="autocomplete"
            ref={searchWrapper}
            {...autocomplete.getRootProps({
                'aria-labelledby': 'autocomplete-label',
            })}
        >
            <ErrorBoundary>
                <SearchBox
                    onKeyDown={handleKeyDown}
                    onCancelClick={() => {
                        if (isClearQueryOnCancel) {
                            autocomplete.setQuery('');
                            setAutocompleteQuery('');
                            setSearchTermValue('');
                            setSearchAssistTermValue('');
                            setSearchAssistNameValue('');
                            setIsAssistTermModified(false);
                        }

                        setAutocompleteState(prev => ({
                            ...prev,
                            isOpen: false,
                            ...(isClearQueryOnCancel ? { collections: [] } : {}),
                        }));
                    }}
                    autocomplete={autocomplete}
                    query={autocompleteQuery}
                    setAutocompleteQuery={setAutocompleteQuery}
                />
            </ErrorBoundary>
            {autocompleteState.isOpen && (
                <Panel
                    onKeyDown={handleKeyDown}
                    autocomplete={autocomplete}
                    collections={collections}
                    highlightedIndex={cursor}
                    query={autocompleteQuery}
                    optexProducts={optexProducts}
                />
            )}
        </div>
    );
}
